import { useMemo, useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import BrushIcon from "@mui/icons-material/Brush";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PublicIcon from "@mui/icons-material/Public";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import Badge from "@mui/material/Badge";
import GroupIcon from "@mui/icons-material/Group";
import { useMsal } from "@azure/msal-react";
import { DEV_AUTH_BYPASS } from "@/devAuthBypass";
import { usePublicConfig } from "@/hooks/queries/usePublicConfig";
import { Breadcrumbs } from "@components/Breadcrumbs";
import { BrandingSection } from "./BrandingSection";
import { CategoriesSection } from "./CategoriesSection";
import { PollstarSection } from "./PollstarSection";
import { ProfileCategoriesSection } from "./ProfileCategoriesSection";
import { usePollstarPendingCount } from "@/hooks/queries/usePollstar";
import { useConfig } from "@/hooks/queries/useConfig";
import { UserManagementSection } from "./UserManagementSection";
import styles from "./styles.module.css";

type Section = "rep" | "role" | "market" | "country" | "categories" | "pollstar" | "branding" | "users";

const BRANDING_ALLOWED_EMAILS = ["pdhin@christielites.com"];

const ALL_SECTIONS: { key: Section; label: string; icon: React.ReactNode }[] = [
  { key: "rep", label: "Rep", icon: <PersonIcon /> },
  { key: "role", label: "Role", icon: <WorkIcon /> },
  { key: "market", label: "Market", icon: <StorefrontIcon /> },
  { key: "country", label: "Country", icon: <PublicIcon /> },
  { key: "categories", label: "Opportunity Categories", icon: <ViewColumnIcon /> },
  { key: "pollstar", label: "Pollstar", icon: <MusicNoteIcon /> },
  { key: "users", label: "User Management", icon: <GroupIcon /> },
  { key: "branding", label: "Branding", icon: <BrushIcon /> },
];

const PROFILE_CATEGORY_SECTIONS: Record<string, string> = {
  rep: "Rep",
  role: "Role",
  market: "Market",
  country: "Country",
};

export const AppConfig = () => {
  const { instance } = useMsal();
  const { data: publicConfig } = usePublicConfig();
  const isLocalAuth = publicConfig?.authMode === "Local";
  const userEmail = DEV_AUTH_BYPASS
    ? "dev@test.com"
    : instance.getActiveAccount()?.username?.toLowerCase() ?? "";
  const canSeeBranding = isLocalAuth || BRANDING_ALLOWED_EMAILS.includes(userEmail);

  // Pollstar is a per-instance feature (Pollstar:Enabled); hide its tab where the instance has it off.
  // User Management is only meaningful for Local (CIAM) auth; EntraGroups instances manage users in Entra.
  const { data: config } = useConfig();
  const pollstarEnabled = config?.pollstar_enabled ?? false;

  const sections = useMemo(() => {
    let filtered = ALL_SECTIONS;
    if (!canSeeBranding) filtered = filtered.filter((s) => s.key !== "branding");
    if (!pollstarEnabled) filtered = filtered.filter((s) => s.key !== "pollstar");
    if (!isLocalAuth) filtered = filtered.filter((s) => s.key !== "users");
    return filtered;
  }, [canSeeBranding, pollstarEnabled, isLocalAuth]);

  const [activeSection, setActiveSection] = useState<Section>("rep");
  // Don't probe the (now-empty/disabled) mapping tables when the feature is off.
  const { data: pendingCount = 0 } = usePollstarPendingCount(pollstarEnabled);

  const profileCategoryName = PROFILE_CATEGORY_SECTIONS[activeSection];

  return (
    <>
      <Breadcrumbs width="1100px" pageName="App Configuration" />
      <div className={styles.container}>
        <h1>App Configuration</h1>
        <div className={styles.layout}>
          <div className={styles.nav}>
            <List dense>
              {sections.map((s) => (
                <ListItemButton
                  key={s.key}
                  selected={activeSection === s.key}
                  onClick={() => setActiveSection(s.key)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{s.icon}</ListItemIcon>
                  {s.key === "pollstar" && pendingCount > 0 ? (
                    <ListItemText
                      primary={
                        <Badge
                          color="error"
                          badgeContent={pendingCount}
                          max={99}
                          sx={{ "& .MuiBadge-badge": { right: -18, top: 10 } }}
                        >
                          {s.label}
                        </Badge>
                      }
                    />
                  ) : (
                    <ListItemText primary={s.label} />
                  )}
                </ListItemButton>
              ))}
            </List>
          </div>
          <div className={styles.content}>
            {profileCategoryName && <ProfileCategoriesSection categoryName={profileCategoryName} />}
            {activeSection === "categories" && <CategoriesSection />}
            {activeSection === "pollstar" && <PollstarSection />}
            {activeSection === "users" && <UserManagementSection />}
            {activeSection === "branding" && <BrandingSection />}
          </div>
        </div>
      </div>
    </>
  );
};
